import React, { Suspense, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { CancelOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { ActionItemKeys, RejectedCollaborativeAgreement } from '@uplift/api';
import { practiceStateSetupRoute } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { trackToDoCTA } from '@uplift/hooks';
import { useCollaborativeStateDetailsQuery, useProviderLicensesStatusQuery } from '@uplift/queries';
import { UserRole } from '@uplift/types';
import { BaseDashboardActionItem } from '@uplift/ui-v2';
import { CamelCasedPropertiesDeep } from 'type-fest';
import { collaborativeAgreementRejectionReasonText, licenseAllowsStateExhibit, UploadCollaborativeAgreementModal, useCanResolveRejectionWithStateExhibit } from '@uplift/pages';

export type CollabAgreementRejectedProps = {
  agreement: CamelCasedPropertiesDeep<RejectedCollaborativeAgreement>;
  onDismiss: (agreementId: string) => void;
};

export const CollabAgreementRejected: React.VFC<CollabAgreementRejectedProps> = ({ agreement, onDismiss }) => (
  <Suspense fallback={<CollabAgreementRejectedContent agreement={agreement} onDismiss={onDismiss} canResolveWithExhibit={undefined} />}>
    <CollabAgreementRejectedResolver agreement={agreement} onDismiss={onDismiss} />
  </Suspense>
);

const CollabAgreementRejectedResolver: React.VFC<CollabAgreementRejectedProps> = ({ agreement, onDismiss }) => {
  const me = useCurrentuser();
  const { data: licenses } = useProviderLicensesStatusQuery(me.id);
  const license = licenses?.find(l => l.id === agreement.licenseId);
  const { data: collaborativeStateDetails } = useCollaborativeStateDetailsQuery(agreement.stateId);
  const canResolveWithExhibit = useCanResolveRejectionWithStateExhibit(
    me.id,
    agreement.licenseId,
    agreement.stateId,
    licenseAllowsStateExhibit(license, collaborativeStateDetails)
  );

  return <CollabAgreementRejectedContent agreement={agreement} onDismiss={onDismiss} canResolveWithExhibit={canResolveWithExhibit} />;
};

type CollabAgreementRejectedContentProps = CollabAgreementRejectedProps & {
  canResolveWithExhibit: boolean | undefined;
};

const CollabAgreementRejectedContent: React.VFC<CollabAgreementRejectedContentProps> = ({ agreement, onDismiss, canResolveWithExhibit }) => {
  const [ uploadModalOpen, setUploadModalOpen ] = useState(false);
  const me = useCurrentuser();
  const queryClient = useQueryClient();
  const history = useHistory();

  const handleUploadModalClose = () => {
    setUploadModalOpen(false);
    queryClient.invalidateQueries({ queryKey: [ 'allActionItems' ] });
  };

  const reasonText = collaborativeAgreementRejectionReasonText(agreement.rejectionReason, agreement.rejectionDetails);

  const handleActionClick = () => {
    trackToDoCTA({
      toDoType: ActionItemKeys.PROVIDER_COLLABORATIVE_AGREEMENT_REJECTED,
      userType: UserRole.provider
    });

    if (canResolveWithExhibit) {
      history.push(practiceStateSetupRoute.createLink({ stateId: agreement.stateId }));
      return;
    }

    setUploadModalOpen(true);
  };

  return (
    <>
      <BaseDashboardActionItem
        icon={<CancelOutlined />}
        color="danger"
        title="Collaborative agreement rejected"
        body={(
          <>
            <Typography variant="body2" color="textPrimary">
              {agreement.stateName ? `${agreement.stateName} License` : 'License'}: {reasonText}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Review the reason and resubmit your updated agreement.
            </Typography>
          </>
        )}
        action={canResolveWithExhibit === undefined ? undefined : {
          text: canResolveWithExhibit ? 'Resolve' : 'Upload New PDF',
          handleClick: handleActionClick
        }}
        dismiss={{
          text: 'Mark as Viewed',
          handleClick: () => onDismiss(agreement.agreementId)
        }}
      />
      {uploadModalOpen && (
        <UploadCollaborativeAgreementModal
          onClose={handleUploadModalClose}
          license={{
            id: agreement.licenseId,
            stateId: agreement.stateId,
            userId: me.id
          }}
        />
      )}
    </>
  );
};
